/**
 * Fiche élève vue par le prof : ce qui mérite son attention en haut de page
 * (décrochage, devoirs en retard, prochain rendez-vous). Pur et testé.
 */

import { RENDEZ_VOUS_KIND_LABELS } from '@jeraisonne/db';
import {
  INACTIVE_DAYS,
  dayLabel,
  daysSince,
  timeLabel,
  type AgendaItem,
} from './dashboard-overview';

export type FicheAlert = {
  tone: 'danger' | 'warning' | 'info' | 'ok';
  text: string;
};

export type FicheAssignment = {
  id: string;
  due_at: string | null;
};

export function isOverdue(a: FicheAssignment, nowMs: number): boolean {
  return !!a.due_at && new Date(a.due_at).getTime() < nowMs;
}

/** Devoirs en cours : en retard d'abord (le plus ancien en tête), puis par échéance. */
export function sortActiveAssignments<T extends FicheAssignment>(
  list: readonly T[],
  nowMs: number,
): T[] {
  const due = (a: T) => (a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY);
  return [...list].sort((a, b) => {
    const la = isOverdue(a, nowMs) ? 0 : 1;
    const lb = isOverdue(b, nowMs) ? 0 : 1;
    return la - lb || due(a) - due(b);
  });
}

/**
 * Bandeau d'état sous le nom : 1 à 3 lignes courtes, la plus urgente d'abord.
 * Élève régulier sans retard ni rendez-vous → une ligne « tout va bien ».
 */
export function studentAlerts(input: {
  lastActivityAt: string | null;
  overdue: number;
  nextRendezVous: Pick<AgendaItem, 'startsAt' | 'kind' | 'status'> | null;
  nowMs: number;
}): FicheAlert[] {
  const out: FicheAlert[] = [];
  if (input.overdue > 0)
    out.push({
      tone: 'danger',
      text: `${input.overdue} devoir${input.overdue > 1 ? 's' : ''} en retard`,
    });
  const days = daysSince(input.lastActivityAt, input.nowMs);
  if (days === null) out.push({ tone: 'warning', text: 'N’a encore fait aucun exercice' });
  else if (days >= INACTIVE_DAYS)
    out.push({ tone: 'warning', text: `Aucune activité depuis ${days} jours` });
  if (input.nextRendezVous) {
    const r = input.nextRendezVous;
    const when = `${dayLabel(r.startsAt, input.nowMs).toLowerCase()} à ${timeLabel(r.startsAt)}`;
    out.push({
      tone: 'info',
      text: `Prochain rendez-vous ${when} · ${RENDEZ_VOUS_KIND_LABELS[r.kind]}${r.status === 'pending_payment' ? ' (paiement en attente)' : ''}`,
    });
  }
  if (out.length === 0) out.push({ tone: 'ok', text: 'Régulier, aucun devoir en retard' });
  return out;
}
